import { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SkipLink, ScreenReaderAnnouncer, Spinner, ErrorBoundary, ToastProvider } from '@/components/ui'
import { AuthProvider, ProtectedRoute, GuestRoute } from '@/components/auth'
import { ProtectedLayoutWrapper } from '@/components/layout'
import { I18nProvider, ThemeProvider } from '@/hooks'
import { setupKeyboardNavigation } from '@/lib/accessibility'
import { ApiError } from '@/services/api'

// Lazy load pages for code splitting
const NotFoundPage = lazy(() => import('@/pages/404'))
const ServerErrorPage = lazy(() => import('@/pages/500'))

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/login'))
const RegisterPage = lazy(() => import('@/pages/auth/register'))
const VerifyEmailPage = lazy(() => import('@/pages/auth/verify-email'))
const VerifySuccessPage = lazy(() => import('@/pages/auth/verify-success'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/forgot-password'))
const ResetSentPage = lazy(() => import('@/pages/auth/reset-sent'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/reset-password'))
const PasswordChangedPage = lazy(() => import('@/pages/auth/password-changed'))
const AuthCallbackPage = lazy(() => import('@/pages/auth/callback'))

// Protected pages (standalone — no AppLayout)
const WelcomeWizardPage = lazy(() => import('@/pages/welcome'))

// Protected pages
const DashboardPage = lazy(() => import('@/pages/dashboard'))
const RulesPage = lazy(() => import('@/pages/rules'))
const RuleDetailPage = lazy(() => import('@/pages/rules/[id]'))
const RuleMatchesPage = lazy(() => import('@/pages/rule-matches'))
const RuleMatchDetailPage = lazy(() => import('@/pages/rule-matches/[id]'))
const InboxPage = lazy(() => import('@/pages/inbox'))
const InboxDetailPage = lazy(() => import('@/pages/inbox-detail'))
const ArchivePage = lazy(() => import('@/pages/archive'))
const PublicationDetailPage = lazy(() => import('@/pages/archive/publications/[id]'))

// Settings pages
const SettingsPage = lazy(() => import('@/pages/settings/index'))
const SecuritySettingsPage = lazy(() => import('@/pages/settings/security'))
const NotificationSettingsPage = lazy(() => import('@/pages/settings/notifications'))
const WebhookSettingsPage = lazy(() => import('@/pages/settings/webhook'))
const TelegramSettingsPage = lazy(() => import('@/pages/settings/telegram'))
const BillingSettingsPage = lazy(() => import('@/pages/settings/billing'))
const DataSettingsPage = lazy(() => import('@/pages/settings/data'))
const DeleteAccountPage = lazy(() => import('@/pages/settings/delete'))

// Billing pages
const PricingPage = lazy(() => import('@/pages/pricing'))
const CheckoutPage = lazy(() => import('@/pages/checkout/index'))
const UpgradePage = lazy(() => import('@/pages/checkout/upgrade'))
const DowngradePage = lazy(() => import('@/pages/checkout/downgrade'))
const SwitchPlanPage = lazy(() => import('@/pages/checkout/switch'))
const CheckoutSuccessPage = lazy(() => import('@/pages/checkout/success'))
const CheckoutCancelPage = lazy(() => import('@/pages/checkout/cancel'))

/**
 * Check if an error is an AbortError (request was cancelled)
 * AbortErrors should not trigger retries or error UI
 */
function isAbortError(error: unknown): boolean {
  if (error instanceof ApiError && error.isAborted) return true
  if (error instanceof Error) {
    if (error.name === 'AbortError') return true
    if (error.message.includes('AbortError')) return true
    if (error.message.includes('signal is aborted')) return true
  }
  return false
}

/** Redirect legacy /notifications/:id URLs to /rule-matches/:id */
function NotificationRedirect() {
  const { id } = useParams()
  return <Navigate to={`/rule-matches/${id}`} replace />
}

/** Redirect /publications/:id URLs (the old default webhook payload, #373) to /archive/publications/:id */
function PublicationRedirect() {
  const { id } = useParams()
  return <Navigate to={`/archive/publications/${id}`} replace />
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry AbortErrors - they're expected during logout/navigation/tab switches
        if (isAbortError(error)) return false
        // Normal retry logic for other errors
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry AbortErrors for mutations either
        if (isAbortError(error)) return false
        return failureCount < 1 // Mutations get 1 retry by default
      },
    },
  },
})

/**
 * Page loading fallback
 */
function PageLoader() {
  return (
    <div className="h-full flex items-center justify-center bg-gray-50">
      <Spinner size="lg" />
    </div>
  )
}

/**
 * Main App Component
 * Configures providers and routing for the application.
 *
 * Providers (outer to inner):
 * 1. QueryClientProvider - React Query for server state
 * 2. I18nProvider - Internationalization (bg/en)
 * 3. ThemeProvider - Light/Dark/System theme
 * 4. BrowserRouter - React Router
 * 5. AuthProvider - Authentication context
 * 6. ScreenReaderAnnouncer - Accessibility announcements
 */
function App() {
  // Setup keyboard navigation detection on mount
  useEffect(() => {
    const cleanup = setupKeyboardNavigation()
    return cleanup
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
      <ThemeProvider>
        <ToastProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScreenReaderAnnouncer>
              <ErrorBoundary>
              <div className="h-full bg-gray-50">
                {/* Skip link for accessibility - TF50 */}
                <SkipLink />

                {/* Main content with route-based code splitting */}
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Root redirects to dashboard (landing is on lexalert.bg) */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />

                    {/* Guest-only Routes (redirect to dashboard if authenticated) */}
                    <Route
                      path="/login"
                      element={
                        <GuestRoute>
                          <LoginPage />
                        </GuestRoute>
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <GuestRoute>
                          <RegisterPage />
                        </GuestRoute>
                      }
                    />
                    <Route
                      path="/verify-email"
                      element={
                        <GuestRoute>
                          <VerifyEmailPage />
                        </GuestRoute>
                      }
                    />
                    <Route path="/verify-success" element={<VerifySuccessPage />} />
                    <Route
                      path="/forgot-password"
                      element={
                        <GuestRoute>
                          <ForgotPasswordPage />
                        </GuestRoute>
                      }
                    />
                    <Route
                      path="/reset-sent"
                      element={
                        <GuestRoute>
                          <ResetSentPage />
                        </GuestRoute>
                      }
                    />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/password-changed" element={<PasswordChangedPage />} />

                    {/* Protected standalone route (no AppLayout) */}
                    <Route
                      path="/welcome"
                      element={
                        <ProtectedRoute>
                          <WelcomeWizardPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Protected Routes with Layout */}
                    <Route
                      element={
                        <ProtectedRoute>
                          <ProtectedLayoutWrapper />
                        </ProtectedRoute>
                      }
                    >
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/rules" element={<RulesPage />} />
                      <Route path="/rules/create" element={<RulesPage />} />
                      <Route path="/rules/:id" element={<RuleDetailPage />} />
                      <Route path="/rule-matches" element={<RuleMatchesPage />} />
                      <Route path="/rule-matches/:id" element={<RuleMatchDetailPage />} />
                      <Route path="/notifications" element={<Navigate to="/rule-matches" replace />} />
                      <Route path="/notifications/:id" element={<NotificationRedirect />} />
                      <Route path="/inbox" element={<InboxPage />} />
                      <Route path="/inbox/:id" element={<InboxDetailPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/settings/security" element={<SecuritySettingsPage />} />
                      <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                      <Route path="/settings/webhook" element={<WebhookSettingsPage />} />
                      <Route path="/settings/telegram" element={<TelegramSettingsPage />} />
                      <Route path="/settings/billing" element={<BillingSettingsPage />} />
                      <Route path="/settings/data" element={<DataSettingsPage />} />
                      <Route path="/settings/delete" element={<DeleteAccountPage />} />
                      <Route path="/archive" element={<ArchivePage />} />
                      <Route path="/archive/search" element={<ArchivePage />} />
                      <Route path="/archive/publications/:id" element={<PublicationDetailPage />} />
                      <Route path="/publications/:id" element={<PublicationRedirect />} />
                      {/* Billing pages */}
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/checkout/upgrade" element={<UpgradePage />} />
                      <Route path="/checkout/downgrade" element={<DowngradePage />} />
                      <Route path="/checkout/switch" element={<SwitchPlanPage />} />
                      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
                    </Route>

                    {/* Error Pages */}
                    <Route path="/404" element={<NotFoundPage />} />
                    <Route path="/500" element={<ServerErrorPage />} />

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </Suspense>
              </div>
              </ErrorBoundary>
            </ScreenReaderAnnouncer>
          </AuthProvider>
        </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  )
}

export default App
